import * as Yup from 'yup';
import { PROHIBITED_WORDS } from 'legacyfrontend/constants/prohibitedWords';

const hasExcessiveCharacterRepetition = (value) => {
  if (!value || typeof value !== 'string') return false;
  return /(.)\1{3,}/.test(value);
};

const isOnlyInitials = (value) => {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  const letterCount = trimmed.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ]/g, '').length;
  return letterCount <= 2 && letterCount > 0;
};

const containsProhibitedWord = (value) => {
  if (!value || typeof value !== 'string' || PROHIBITED_WORDS.length === 0) return false;
  const normalizedValue = value.toLowerCase().trim();

  const foundWord = PROHIBITED_WORDS.find((word) => {
    if (!word || typeof word !== 'string') return false;
    const normalizedWord = word.toLowerCase().trim();

    // Para palabras muy cortas (1-2 caracteres), solo rechazar si el valor completo es exactamente esa palabra
    if (normalizedWord.length <= 2) {
      return normalizedValue === normalizedWord;
    }

    // Para palabras más largas, buscar como palabra completa usando word boundaries
    const wordBoundaryRegex = new RegExp(`\\b${normalizedWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return wordBoundaryRegex.test(normalizedValue);
  });

  return !!foundWord;
};

const validateNameField = (value, otherValue = '') => {
  if (!value || typeof value !== 'string') {
    return true;
  }

  const trimmedValue = value.trim();

  if (hasExcessiveCharacterRepetition(trimmedValue)) {
    return false;
  }

  if (isOnlyInitials(trimmedValue)) {
    return false;
  }

  if (otherValue && trimmedValue.toLowerCase().trim() === otherValue.toLowerCase().trim() && trimmedValue.length > 0) {
    return false;
  }

  if (containsProhibitedWord(trimmedValue)) {
    return false;
  }

  return true;
};

export const validateAge = (birthDate, passengerType) => {
  if (!birthDate) {
    return false;
  }

  const today = new Date();
  const birth = new Date(birthDate);

  today.setHours(0, 0, 0, 0);
  birth.setHours(0, 0, 0, 0);

  if (birth > today) {
    return false;
  }

  let years = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    years--;
  }

  switch (passengerType) {
    case 'INF':
      if (years >= 2) {
        return false;
      }
      return true;
    case 'CHD':
      if (years < 2) {
        return false;
      }
      if (years > 11) {
        return false;
      }
      return true;
    case 'ADT':
      if (years < 12) {
        return false;
      }
      return true;
    default:
      return false;
  }
};

/**
 * Genera un esquema de validación dinámico para pasajeros basado en la configuración
 * @param {Object} passengerConfig - Configuración del pasajero proveniente de la API
 * @param {string} passengerType - Tipo de pasajero (ADT, CHD, INF, etc.)
 * @param {boolean} isAdult - Indica si el pasajero es adulto
 * @returns {Object} Esquema de validación Yup
 */
export const generatePassengerSchema = (passengerConfig, passengerType = 'ADT', isAdult = true) => {
  if (!passengerConfig || !passengerConfig.passenger) {
    return getDefaultPassengerSchema(isAdult, passengerType);
  }

  const requiredFields = passengerConfig.passenger.requiredFields[passengerType] || [];

  const schema = {
    gender: Yup.string().when('$isAdult', {
      is: true,
      then: (schema) =>
        schema
          .oneOf(['Male', 'Female'], 'El género debe ser Male o Female')
          .required('El género es requerido'),
      otherwise: (schema) => schema.optional(),
    }),

    name: Yup.string()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .required('El nombre es requerido')
      .test(
        'name-validation',
        'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
        function (value) {
          const lastName = this.parent.lastName || '';
          return validateNameField(value, lastName);
        }
      ),

    lastName: Yup.string()
      .min(2, 'Los apellidos deben tener al menos 2 caracteres')
      .max(41, 'Máximo 41 caracteres')
      .required('Los apellidos son requeridos')
      .test(
        'lastname-validation',
        'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
        function (value) {
          const name = this.parent.name || '';
          return validateNameField(value, name);
        }
      ),

    frequentFlyer: Yup.string().optional(),

    birthDate: Yup.date()
      .required('La fecha de nacimiento es requerida')
      .test('age-validation', 'La edad no corresponde con el tipo de pasajero', (value) => {
        return validateAge(value, passengerType);
      }),

    documentType: Yup.string().required('El tipo de documento es requerido'),

    documentNumber: Yup.string().test(
      'validate-doc',
      'Formato de documento inválido',
      function (value) {
        const docType = this.parent.documentType;

        if (!value && !requiredFields.includes('doc_number')) {
          return true;
        }

        if (docType === 'dni') {
          return /^\d{7,8}$/.test(value);
        }

        if (docType === 'pasaporte') {
          const upperValue = value?.toUpperCase() || '';
          return /^[A-Z0-9]{6,12}$/.test(upperValue);
        }

        if (docType === 'cedula') {
          return /^\d{6,10}$/.test(value);
        }

        return true;
      }
    ),

    issueDate: Yup.date().optional(),
    issuedIn: Yup.string().optional(),
    nationality: Yup.string().optional(),
    expiryDate: Yup.date().optional(),

    ctclType: Yup.string(),
    ctclNumber: Yup.string(),
  };
  if (requiredFields.includes('name')) {
    schema.name = schema.name.required('El nombre es requerido');
  }

  if (requiredFields.includes('surname')) {
    schema.lastName = schema.lastName.required('Los apellidos son requeridos');
  }

  if (requiredFields.includes('birthdate')) {
    schema.birthDate = schema.birthDate.required('La fecha de nacimiento es requerida');
  }

  if (requiredFields.includes('doc_type')) {
    schema.documentType = schema.documentType.required('El tipo de documento es requerido');
  }

  if (requiredFields.includes('doc_number')) {
    schema.documentNumber = schema.documentNumber.required('El número de documento es requerido');
  }

  if (requiredFields.includes('frequent_traveller')) {
    schema.frequentFlyer = schema.frequentFlyer.required(
      'El número de viajero frecuente es requerido'
    );
  }

  if (requiredFields.includes('issue_date')) {
    schema.issueDate = schema.issueDate.required('La fecha de expedición es requerida');
  }

  if (requiredFields.includes('issued_in')) {
    schema.issuedIn = schema.issuedIn.required('El lugar de expedición es requerido');
  }

  if (requiredFields.includes('nationality')) {
    schema.nationality = schema.nationality.required('La nacionalidad es requerida');
  }

  if (requiredFields.includes('expiry_date')) {
    schema.expiryDate = schema.expiryDate.required('La fecha de caducidad es requerida');
  }

  if (requiredFields.includes('ctcl_type')) {
    schema.ctclType = schema.ctclType.required('El tipo de documento fiscal es requerido');
  }

  if (requiredFields.includes('ctcl_number')) {
    schema.ctclNumber = schema.ctclNumber.required('El número de documento fiscal es requerido');
  }

  return Yup.object(schema);
};

const getDefaultPassengerSchema = (isAdult = true, passengerType = 'ADT') => {
  return Yup.object({
    gender: isAdult ? Yup.string().required('El género es requerido') : Yup.string(),
    name: Yup.string()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .required('El nombre es requerido')
      .test(
        'name-validation',
        'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
        function (value) {
          const lastName = this.parent.lastName || '';
          return validateNameField(value, lastName);
        }
      ),
    lastName: Yup.string()
      .min(2, 'Los apellidos deben tener al menos 2 caracteres')
      .max(41, 'Máximo 41 caracteres')
      .required('Los apellidos son requeridos')
      .test(
        'lastname-validation',
        'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
        function (value) {
          const name = this.parent.name || '';
          return validateNameField(value, name);
        }
      ),
    frequentFlyer: Yup.string(),
    birthDate: Yup.date()
      .required('La fecha de nacimiento es requerida')
      .test('age-validation', 'La edad no corresponde con el tipo de pasajero', (value) => {
        return validateAge(value, passengerType);
      }),
    documentType: Yup.string().required('El tipo de documento es requerido'),
    documentNumber: Yup.string()
      .required('El número de documento es requerido')
      .test('validate-doc', 'Formato de documento inválido', function (value) {
        const docType = this.parent.documentType;

        if (!value) {
          return false;
        }

        if (docType === 'dni') {
          return /^\d{7,8}$/.test(value);
        }

        if (docType === 'pasaporte') {
          return /^[A-Z0-9]{6,12}$/.test(value.toUpperCase());
        }

        if (docType === 'cedula') {
          return /^\d{6,10}$/.test(value);
        }

        return true;
      }),
    issueDate: Yup.date(),
    issuedIn: Yup.string(),
    nationality: Yup.string(),
    expiryDate: Yup.date(),
  });
};

export const passengerSchema = getDefaultPassengerSchema();

export const emergencyContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'El nombre debe tener al menos 2 caracteres')
    .test(
      'name-validation',
      'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
      function (value) {
        const lastName = this.parent.lastName || '';
        return validateNameField(value, lastName);
      }
    ),
  lastName: Yup.string()
    .min(2, 'Los apellidos deben tener al menos 2 caracteres')
    .max(41, 'El número máximo de caracteres es de 41')
    .test(
      'lastname-validation',
      'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
      function (value) {
        const name = this.parent.name || '';
        return validateNameField(value, name);
      }
    ),
  phone: Yup.object().shape({
    codigo: Yup.string().required('El código de país es requerido'),
    numero: Yup.string()
      .transform((value) => value?.replace(/\D/g, ''))
      .matches(/^[0-9]{8,}$/, 'Solo se permiten números')
      .required('El número de teléfono es requerido'),
  }),
  country: Yup.string(),
});

export const generateFiscalDataSchema = (documentOptions = ['CUIL', 'CUIT'], documentName = 'CUIT/CUIL') => {
  const validDocumentOptions = documentOptions && documentOptions.length > 0 ? documentOptions : ['CUIL', 'CUIT'];
  const documentList = validDocumentOptions.join(' o ');
  
  return Yup.object().shape({
    documentType: Yup.string().when('$isInternational', {
      is: true,
      then: (schema) =>
        schema
          .required('El tipo de documento es requerido')
          .oneOf(validDocumentOptions, `Debe seleccionar ${documentList}`),
      otherwise: (schema) => schema.nullable(),
    }),
    cuitCuil: Yup.string().when('$isInternational', {
      is: true,
      then: (schema) => schema.required(`El ${documentName.toLowerCase()} es requerido`),
      otherwise: (schema) => schema.nullable(),
    }),
  });
};

export const fiscalDataSchema = generateFiscalDataSchema();

/**
 * Genera un esquema de validación dinámico basado en la configuración de contacto de la API
 * @param {Object} contactConfig - Configuración de contacto de la API
 * @returns {Object} - Esquema de validación de Yup
 */
export const generateContactSchema = (contactConfig) => {
  const { fields = [], requiredFields = [], mandatory = false } = contactConfig || {};
  const schema = {};

  const fieldMapping = {
    name: 'nombre',
    surname: 'apellidos',
    email: 'email',
    phone: 'telefono1',
    phone2: 'telefono2',
  };

  const validationRules = {
    nombre: Yup.string()
      .min(2, 'El nombre debe tener al menos 2 caracteres')
      .test(
        'name-validation',
        'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
        function (value) {
          const apellidos = this.parent.apellidos || '';
          return validateNameField(value, apellidos);
        }
      ),

    apellidos: Yup.string()
      .min(2, 'Los apellidos deben tener al menos 2 caracteres')
      .max(41, 'El número máximo de caracteres es de 41')
      .test(
        'lastname-validation',
        'El nombre o apellido ingresado no es válido. Por favor ingresá datos reales del pasajero.',
        function (value) {
          const nombre = this.parent.nombre || '';
          return validateNameField(value, nombre);
        }
      ),

    email: Yup.string().matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
      'Email inválido'
    ),

    telefono1: {
      codigo: Yup.string(),
      numero: Yup.string()
        .transform((value) => value?.replace(/\D/g, ''))
        .matches(/^[0-9]{8,}$/, 'El número ingresado no es valido'),
    },

    telefono2: {
      codigo: Yup.string(),
      numero: Yup.string()
        .transform((value) => value?.replace(/\D/g, ''))
        .matches(/^[0-9]{8,}$/, 'El número ingresado no es valido')
        .test('is-empty-or-valid', 'El número debe tener al menos 8 dígitos', (value) => {
          return !value || value.length >= 8;
        }),
    },
  };

  fields.forEach((field) => {
    const fieldName = fieldMapping[field.name];
    if (!fieldName) return;

    const isRequired = requiredFields.includes(field.name);

    if (fieldName === 'nombre') {
      schema[fieldName] = validationRules.nombre.clone();
      if (isRequired) {
        schema[fieldName] = schema[fieldName].required('El nombre es requerido');
      }
    } else if (fieldName === 'apellidos') {
      schema[fieldName] = validationRules.apellidos.clone();
      if (isRequired) {
        schema[fieldName] = schema[fieldName].required('Los apellidos son requeridos');
      }
    } else if (fieldName === 'email') {
      schema[fieldName] = validationRules.email.clone();
      if (isRequired) {
        schema[fieldName] = schema[fieldName].required('El email es requerido');
      }
    } else if (fieldName === 'telefono1') {
      if (isRequired) {
        schema[fieldName] = Yup.object({
          codigo: validationRules.telefono1.codigo.required('El código de país es requerido'),
          numero: validationRules.telefono1.numero.required('El número es requerido'),
        });
      } else {
        schema[fieldName] = Yup.object({
          codigo: validationRules.telefono1.codigo,
          numero: validationRules.telefono1.numero,
        });
      }
    } else if (fieldName === 'telefono2') {
      schema[fieldName] = Yup.object({
        codigo: validationRules.telefono2.codigo,
        numero: validationRules.telefono2.numero,
      });
    }
  });

  return Yup.object().shape(schema);
};

/**
 * Genera los valores iniciales del formulario con campos fijos y solo phone2 opcional
 * @param {Object} contactConfig - Configuración de contacto de la API
 * @returns {Object} - Valores iniciales del formulario
 */
export const generateInitialValues = (contactConfig) => {
  const fields = contactConfig?.fields || [];
  const hasPhone2 = fields.some((field) => field.name === 'phone2');

  return {
    nombre: '',
    apellidos: '',
    email: '',
    telefono1: {
      numero: '',
    },
    ...(hasPhone2 && {
      telefono2: {
        numero: '',
      },
    }),
  };
};
